import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { vilniusToCalendarUTC } from './dateUtils';
import {
  createDefaultSlotObject,
  getLocationDisplayName,
  hasReservationConflictForLocation,
  isSlotBookable as isSlotBookableVenue,
  normalizeTimeSlots,
  stripLegacyAvailability,
  getVenuesAvailableInSlot,
  LOCATIONS as VENUE_LOCATIONS
} from './venueCalendarUtils';

export {
  TRAINING_VENUES,
  ALL_TRAINING_VENUE_IDS,
  DEFAULT_COACH_EDIT_VENUE_IDS,
  createEmptyVenuesMap,
  createDefaultSlotObject,
  normalizeSlot,
  normalizeTimeSlots,
  stripLegacyAvailability,
  coachToggleSlot,
  getActiveVenuesForCoach,
  getSelectedVenuesForEdit,
  getVenuesAvailableInSlot,
  getSlotVenueBackground,
  buildVenueDiagonalGradient,
  getVenueIdFromDiagonalClick,
  getLocationDisplayName,
  isHalfHourBlockedByReservations,
  isVenueAvailableForDuration,
  getReservationCoveringSlot,
  isSlotCoveredByAnyReservation,
  getReservationBlockMinutes,
  hasReservationConflictForLocation
} from './venueCalendarUtils';

// Collection names – available slots are stored per coach: availableSlots/{coachId}/dates/{date}
const AVAILABLE_SLOTS_DATES = 'dates';
export const COLLECTIONS = {
  AVAILABLE_SLOTS: 'availableSlots',
  RESERVATIONS: 'reservations',
  RESERVATION_LOG: 'reservationLog',
  COURTS: 'courts',
  COURT_PRICES: 'courtPrices',
  MAIL: 'mail',
  CLIENT_REPORTS: 'clientReports',
  COACHES: 'coaches',
  COACH_RECURRING_RANGES: 'coachRecurringRanges'
};

// Pricing: 1 person 40 €/h, 2 persons 45 €/h, 3–4 persons 50 €/h. Court hire separate (+ Aikštelės nuomos kaina).
export const PRICING = {
  ONE_PERSON_PER_HOUR: 40,
  TWO_PEOPLE_PER_HOUR: 45,
  THREE_FOUR_PERSONS_PER_HOUR: 50
};

// Training price per hour by number of people (uses default PRICING)
export const getTrainingPricePerHour = (numberOfPeople) => {
  if (numberOfPeople <= 1) return PRICING.ONE_PERSON_PER_HOUR;
  if (numberOfPeople === 2) return PRICING.TWO_PEOPLE_PER_HOUR;
  return PRICING.THREE_FOUR_PERSONS_PER_HOUR; // 3 or 4
};

// Coach-specific pricing: { price1Person, price2People, price3People, price4People } in €/h
export const getTrainingPricePerHourWithPricing = (numberOfPeople, pricing) => {
  if (!pricing || typeof pricing !== 'object') return getTrainingPricePerHour(numberOfPeople);
  const p1 = Number(pricing.price1Person);
  const p2 = Number(pricing.price2People);
  const p3 = Number(pricing.price3People);
  const p4 = Number(pricing.price4People);
  if (numberOfPeople <= 1 && !Number.isNaN(p1)) return p1;
  if (numberOfPeople === 2 && !Number.isNaN(p2)) return p2;
  if (numberOfPeople === 3 && !Number.isNaN(p3)) return p3;
  if (numberOfPeople >= 4 && !Number.isNaN(p4)) return p4;
  return getTrainingPricePerHour(numberOfPeople);
};

// Total training price (uses coach pricing when provided)
export const calculateTrainingTotal = (duration, numberOfPeople, pricing) => {
  const hours = duration / 60;
  const pricePerHour = pricing
    ? getTrainingPricePerHourWithPricing(numberOfPeople, pricing)
    : getTrainingPricePerHour(numberOfPeople);
  return pricePerHour * hours;
};

// Coaches: id, uid, display name, email, photo (optional), sebOnly; defaultDescription used when coachProfiles has none
// Elvis coach uid (admin is separate: w4JeMlHRtue6uPPAZKeOgLfKJkL2)
export const COACHES = [
  { id: 'elvis', uid: 'BHZIihfnBfcNhk7segxjHyvqLa82', name: 'Elvis Gotovskis', email: 'gotovskiselvis@gmail.com', photoURL: '/main2.jpg', sebOnly: false, defaultDescription: 'Esu teniso treneris, turintis beveik 30 metų patirtį tenise. Lietuvos čempionatų dalyvis, bei prizininkas, tarptautinių turnyrų dalyvis. Daugiau nei 10 metų mokinu žaisti tenisą žmones, nepriklausomai nuo jų žaidimo lygio ar amžiaus. Vedu grupines, individualias ir dviejų asmenų treniruotes, pritaikydamas individualų požiūrį kiekvienam mokiniui.', phone: '+37069614152', facebook: 'https://www.facebook.com/elvisgotovskis' },
  { id: 'julija', uid: 'y51Y9x1UdVRp1h3MvE3FyHhRVZ32', name: 'Julija Baltuškienė', email: 'julija.baltuskiene@gmail.com', photoURL: '/coach-julija.png', sebOnly: true, defaultDescription: 'Daugkartinė Lietuvos čempionė, tarptautinių turnyrų nugalėtoja, buvusi Lietuvos moterų teniso rinktinės narė.', phone: '+37060338403' },
  { id: 'vitalijus', uid: 'mQTBgMVx18hzvl84boWQQcIKvOk2', name: 'Vitalijus Dūda', email: 'vitalijus.duda@gmail.com', photoURL: null, sebOnly: true, defaultDescription: 'Aprašymas ir nuotrauka bus įkelti vėliau.' },
  { id: 'juozasp', uid: 'Z4cRbNaVvofpH2ZWtrzOh5lOu3d2', name: 'Juozas Pečiulis', email: '', photoURL: null, sebOnly: true, defaultDescription: 'Aprašymas bus įkeltas vėliau.', phone: '' }
];

export const getCoachById = (id) => COACHES.find(c => c.id === id) || null;
export const getCoachByUid = (uid) => COACHES.find(c => c.uid === uid) || null;
export const DEFAULT_COACH_ID = 'elvis';

/** Fixed order on reservation page: Elvis, Julija, Vitalijus, Juozas; then any other coaches (e.g. from admin) by name. */
const COACH_DISPLAY_ORDER = ['elvis', 'julija', 'vitalijus', 'juozasp'];

const sortCoachesByDisplayOrder = (list) => {
  list.sort((a, b) => {
    const ai = COACH_DISPLAY_ORDER.indexOf(a.id);
    const bi = COACH_DISPLAY_ORDER.indexOf(b.id);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return (a.name || '').localeCompare(b.name || '', 'lt');
  });
  return list;
};

/** Single source of truth for web + mobile: fetch coach list from Firestore (no app store update when adding coaches). */
export const getCoachesFromFirestore = async () => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.COACHES));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('Error fetching coaches from Firestore:', err);
    return [];
  }
};

/** One-time seed: write current COACHES to Firestore if collection is empty (run once by admin). */
export const seedCoachesIfEmpty = async () => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.COACHES));
    if (snapshot.docs.length > 0) return;
    for (const c of COACHES) {
      await setDoc(doc(db, COLLECTIONS.COACHES, c.id), {
        uid: c.uid,
        name: c.name,
        sebOnly: c.sebOnly === true,
        email: c.email || '',
        phone: c.phone || ''
      });
    }
  } catch (err) {
    console.error('Error seeding coaches:', err);
    throw err;
  }
};

/** Add an approved coach to the Firestore coaches list (so web + mobile see them without app update). */
export const addCoachToFirestoreList = async (uid, data) => {
  try {
    await setDoc(doc(db, COLLECTIONS.COACHES, uid), {
      uid,
      name: data.name || data.fullName || '—',
      sebOnly: data.sebOnly === true,
      email: data.email || '',
      phone: data.phone || data.phoneNumber || ''
    });
  } catch (err) {
    console.error('Error adding coach to Firestore list:', err);
    throw err;
  }
};

// Coach public profiles (description, photo – editable by coaches, shown on home page)
export const getCoachProfile = async (coachId) => {
  try {
    const docRef = doc(db, 'coachProfiles', coachId);
    const snap = await getDoc(docRef);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (err) {
    console.error('Error getting coach profile:', err);
    return null;
  }
};

export const getCoachProfiles = async () => {
  try {
    const result = {};
    for (const c of COACHES) {
      const p = await getCoachProfile(c.id);
      if (p) result[c.id] = p;
    }
    return result;
  } catch (err) {
    console.error('Error getting coach profiles:', err);
    return {};
  }
};

export const updateCoachProfile = async (coachId, updates) => {
  try {
    const docRef = doc(db, 'coachProfiles', coachId);
    const snap = await getDoc(docRef);
    const data = { ...updates, updatedAt: Timestamp.now() };
    if (snap.exists()) {
      await updateDoc(docRef, data);
    } else {
      await setDoc(docRef, { ...data, createdAt: Timestamp.now() });
    }
    return docRef.id;
  } catch (err) {
    console.error('Error updating coach profile:', err);
    throw err;
  }
};

// Coach URL slugs for sharing (add new coaches here so /rezervacija/slug resolves correctly)
const COACH_SLUGS = {
  'elvis': 'elvis-gotovskis',
  'julija': 'julija-baltuskiene',
  'vitalijus': 'vitalijus-duda',
  'juozasp': 'juozas-pecialis'
};

const COACH_ID_BY_SLUG = {
  'elvis-gotovskis': 'elvis',
  'julija-baltuskiene': 'julija',
  'vitalijus-duda': 'vitalijus',
  'juozas-pecialis': 'juozasp',
  'juozasp': 'juozasp' // allow raw id in URL so links stay valid
};

export const getCoachSlug = (coachId) => COACH_SLUGS[coachId] || coachId || COACH_SLUGS[DEFAULT_COACH_ID];
export const getCoachIdFromSlug = (slug) => COACH_ID_BY_SLUG[slug] || (slug && slug.length >= 20 ? slug : null) || DEFAULT_COACH_ID;

// Location constants
export const LOCATIONS = {
  SEB_ARENA: VENUE_LOCATIONS.SEB_ARENA,
  VRM_COURTS: VENUE_LOCATIONS.VRM_COURTS,
  BERNARDAI: VENUE_LOCATIONS.BERNARDAI
};

export const LOCATION_INFO = {
  [LOCATIONS.SEB_ARENA]: {
    name: 'Seb Arena',
    address: 'Ąžuolyno g. 7, Vilnius',
    googleMapsUrl: 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Ąžuolyno g. 7, Vilnius'),
    mapEmbedUrl: 'https://maps.google.com/maps?q=' + encodeURIComponent('Ąžuolyno g. 7, Vilnius') + '&z=15&output=embed'
  },
  [LOCATIONS.VRM_COURTS]: {
    name: 'VRM teniso kortai',
    address: 'Žirmūnų g. 1e, Vilnius',
    googleMapsUrl: 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('Žirmūnų g. 1e, Vilnius'),
    mapEmbedUrl: 'https://maps.google.com/maps?q=' + encodeURIComponent('Žirmūnų g. 1e, Vilnius') + '&z=15&output=embed'
  },
  [LOCATIONS.BERNARDAI]: {
    name: 'Bernardinų teniso kortai',
    address: 'B. Radvilaitės g. 6C, Vilnius',
    googleMapsUrl: 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent('B. Radvilaitės g. 6C, Vilnius'),
    mapEmbedUrl: 'https://maps.google.com/maps?q=' + encodeURIComponent('B. Radvilaitės g. 6C, Vilnius') + '&z=15&output=embed'
  }
};

/** Get courts from Firestore (admin-managed additional locations) */
export const getCourts = async () => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.COURTS));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting courts:', error);
    return [];
  }
};

/** Get training locations for home page: Seb Arena first, then VRM, then any from courts collection. Optionally filtered by availability. */
export const getTrainingLocations = async () => {
  const locations = [];
  const seen = new Set();
  const order = [LOCATIONS.SEB_ARENA, LOCATIONS.VRM_COURTS, LOCATIONS.BERNARDAI];

  for (const locId of order) {
    const info = LOCATION_INFO[locId];
    if (info && !seen.has(locId)) {
      seen.add(locId);
      locations.push({ id: locId, ...info });
    }
  }

  try {
    const courts = await getCourts();
    for (const court of courts) {
      if (court.locationId && seen.has(court.locationId)) continue;
      const courtId = court.id || court.locationId || `court_${court.id}`;
      if (seen.has(courtId)) continue;
      seen.add(courtId);
      const name = court.name || court.address || 'Vieta';
      const address = court.address || '';
      const googleMapsUrl = court.googleMapsUrl || (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : '');
      const mapEmbedUrl = court.mapEmbedUrl || (address ? `https://maps.google.com/maps?q=${encodeURIComponent(address)}&z=15&output=embed` : '');
      locations.push({
        id: courtId,
        name,
        address,
        googleMapsUrl,
        mapEmbedUrl
      });
    }
  } catch (err) {
    console.error('Error loading courts for locations:', err);
  }

  return locations;
};

// Availability states
export const AVAILABILITY_STATES = {
  UNAVAILABLE: 'unavailable',
  SEB_ARENA: 'seb_arena',
  VRM_COURTS: 'vrm_courts',
  BERNARDAI: 'bernardinai_courts'
};

// Time slot generation
export const generateTimeSlots = (startHour = 7, endHour = 22) => {
  const slots = [];
  for (let hour = startHour; hour < endHour; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

// Legacy: total = training only (court price is not fixed; depends on time and season)
export const calculatePrice = (duration, numberOfPeople) => {
  return calculateTrainingTotal(duration, numberOfPeople);
};

// Per-coach subcollection: availableSlots/{coachId}/dates/{date} – each coach's data in their own "folder"
const getAvailableSlotsDatesRef = (coachId) =>
  collection(db, COLLECTIONS.AVAILABLE_SLOTS, coachId || DEFAULT_COACH_ID, AVAILABLE_SLOTS_DATES);
const getAvailableSlotsDocRef = (coachId, date) =>
  doc(db, COLLECTIONS.AVAILABLE_SLOTS, coachId || DEFAULT_COACH_ID, AVAILABLE_SLOTS_DATES, date);

// Add available time slots for a specific date (default: all unavailable)
export const addAvailableSlots = async (date, courtId = 'court1', coachId = DEFAULT_COACH_ID) => {
  try {
    const slots = generateTimeSlots().map(time => createDefaultSlotObject(time, 30));
    const cId = coachId || DEFAULT_COACH_ID;
    const docRef = getAvailableSlotsDocRef(cId, date);
    await setDoc(docRef, {
      date,
      timeSlots: slots,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return date;
  } catch (error) {
    console.error('Error adding available slots:', error);
    throw error;
  }
};

// Get available slots for a specific date (and coach)
export const getAvailableSlots = async (date, coachId = DEFAULT_COACH_ID) => {
  try {
    const docRef = getAvailableSlotsDocRef(coachId, date);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    return snap.data();
  } catch (error) {
    console.error('Error getting available slots:', error);
    throw error;
  }
};

// Get available slots for a date range for a coach
export const getAvailableSlotsForRange = async (startDate, endDate, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const q = query(
      getAvailableSlotsDatesRef(cId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date')
    );
    const querySnapshot = await getDocs(q);
    const slots = {};
    querySnapshot.forEach(docSnap => {
      slots[docSnap.data().date] = docSnap.data();
    });
    return slots;
  } catch (error) {
    console.error('Error getting available slots for range:', error);
    throw error;
  }
};

// Get available slots for a date range (week view) for a coach
export const getAvailableSlotsForWeek = async (startDate, coachId = DEFAULT_COACH_ID) => {
  try {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    const endStr = endDate.toISOString().split('T')[0];
    return getAvailableSlotsForRange(startDate, endStr, coachId);
  } catch (error) {
    console.error('Error getting available slots for week:', error);
    throw error;
  }
};

// Get available slots for a date range (for checking if coach has any bookable upcoming slots)
export const getAvailableSlotsInRange = async (startDateStr, endDateStr, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const q = query(
      getAvailableSlotsDatesRef(cId),
      where('date', '>=', startDateStr),
      where('date', '<=', endDateStr),
      orderBy('date')
    );
    const querySnapshot = await getDocs(q);
    const slots = {};
    querySnapshot.forEach((docSnap) => {
      slots[docSnap.data().date] = docSnap.data();
    });
    return slots;
  } catch (error) {
    console.error('Error getting available slots in range:', error);
    return {};
  }
};

// Only these count as "bookable" for visibility. Unavailable or any other value = coach hidden if no bookable slots.
const isSlotBookable = (s) => isSlotBookableVenue(s);

/** Local date string (YYYY-MM-DD) for today to avoid UTC off-by-one. */
const getTodayLocalDateString = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** True only if coach has at least one upcoming slot with any venue available. */
export const coachHasUpcomingBookableSlots = async (coachId, weeksAhead = 8) => {
  if (coachId == null || typeof coachId !== 'string' || coachId.trim() === '') return false;
  const startStr = getTodayLocalDateString();
  const end = new Date();
  end.setDate(end.getDate() + 7 * weeksAhead);
  const endStr = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  const slotsByDate = await getAvailableSlotsInRange(startStr, endStr, coachId.trim());
  for (const date in slotsByDate) {
    const timeSlots = slotsByDate[date].timeSlots || [];
    if (timeSlots.some(isSlotBookable)) {
      return true;
    }
  }
  return false;
};

/**
 * Sorts filtered list: Elvis first, then previously visible coaches in order, then reappeared/new coaches at end.
 * @param {Array} filteredList - Coaches that have upcoming bookable slots
 * @param {Array<string>} previousVisibleIds - Coach ids in display order from last time (e.g. from session)
 * @returns Sorted list (Elvis first if present, reappearing at end)
 */
export const sortCoachesWithReappearingAtEnd = (filteredList, previousVisibleIds = []) => {
  if (!Array.isArray(filteredList) || filteredList.length === 0) return [];
  const byId = new Map(filteredList.map((c) => [c.id, c]));
  const prevOrder = Array.isArray(previousVisibleIds) ? previousVisibleIds : [];
  const inPrev = new Set(prevOrder.filter((id) => byId.has(id)));
  const reappeared = filteredList.filter((c) => !inPrev.has(c.id));
  const stillVisible = prevOrder.filter((id) => byId.has(id)).map((id) => byId.get(id));
  const elvis = byId.get(DEFAULT_COACH_ID);
  const restWithoutElvis = stillVisible.filter((c) => c.id !== DEFAULT_COACH_ID);
  const reappearedWithoutElvis = reappeared.filter((c) => c.id !== DEFAULT_COACH_ID);
  const reappearedSorted = reappearedWithoutElvis.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'lt'));
  const result = [];
  if (elvis) result.push(elvis);
  result.push(...restWithoutElvis);
  result.push(...reappearedSorted);
  return result;
};

/**
 * Filters to coaches with at least one upcoming bookable slot within the given window, then sorts: Elvis first, reappearing at end.
 * Used on index and reservation pages; weeksAhead 3 is a safeguard so coaches must have slots in the next 3 weeks to be visible.
 * @param {Array} coachesList - Full coach list
 * @param {{ previousVisibleIds?: Array<string>, weeksAhead?: number }} options - previousVisibleIds for sort order; weeksAhead defaults to 3 for public visibility
 * @returns Promise<Array> Sorted coaches with upcoming slots
 */
export const filterCoachesWithUpcomingSlots = async (coachesList, options = {}) => {
  if (!Array.isArray(coachesList) || coachesList.length === 0) return [];
  const { previousVisibleIds = [], weeksAhead = 3 } = options;
  const listWithId = coachesList.filter((c) => c && (c.id != null && String(c.id).trim() !== ''));
  const checks = await Promise.all(
    listWithId.map(async (c) => ({ coach: c, has: await coachHasUpcomingBookableSlots(c.id, weeksAhead) }))
  );
  const filtered = checks.filter((x) => x.has).map((x) => x.coach);
  return sortCoachesWithReappearingAtEnd(filtered, previousVisibleIds);
};

// Update available slots (admin/coach function). When doc doesn't exist, creates with provided timeSlots so first click shows correct state.
export const updateAvailableSlots = async (date, timeSlots, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const normalizedSlots = normalizeTimeSlots(timeSlots).map(stripLegacyAvailability);
    const docRef = getAvailableSlotsDocRef(cId, date);
    const snap = await getDoc(docRef);
    if (!snap.exists()) {
      await setDoc(docRef, {
        date,
        timeSlots: normalizedSlots,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      return date;
    }
    await updateDoc(docRef, { timeSlots: normalizedSlots, updatedAt: Timestamp.now() });
    return date;
  } catch (error) {
    console.error('Error updating available slots:', error);
    throw error;
  }
};

// Coach recurring availability ranges (from Nuolatiniai laisvi laikai). Used by admin to show ranges > 1 month.
export const setCoachRecurringRange = async (coachId, coachName, validFrom, validTo) => {
  try {
    const fromStr = typeof validFrom === 'string' ? validFrom : validFrom.toISOString().slice(0, 10);
    const toStr = typeof validTo === 'string' ? validTo : validTo.toISOString().slice(0, 10);
    const docId = `${coachId}_${fromStr}_${toStr}`;
    const ref = doc(db, COLLECTIONS.COACH_RECURRING_RANGES, docId);
    await setDoc(ref, {
      coachId,
      coachName: coachName || '',
      validFrom: fromStr,
      validTo: toStr,
      updatedAt: Timestamp.now()
    });
    return docId;
  } catch (error) {
    console.error('Error setting coach recurring range:', error);
    throw error;
  }
};

export const getCoachRecurringRanges = async () => {
  try {
    const snapshot = await getDocs(collection(db, COLLECTIONS.COACH_RECURRING_RANGES));
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting coach recurring ranges:', error);
    return [];
  }
};

// Check for reservation conflicts (per venue, with cross-venue buffer)
export const checkReservationConflict = async (date, startTime, duration, coachId = DEFAULT_COACH_ID, location = null) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    if (!location) {
      throw new Error('LOCATION_REQUIRED');
    }

    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('coachId', '==', cId),
      where('date', '==', date),
      where('status', '!=', 'cancelled')
    );

    const querySnapshot = await getDocs(q);
    const existing = querySnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    const conflict = hasReservationConflictForLocation(date, startTime, duration, location, existing);
    if (conflict.hasConflict) {
      return {
        hasConflict: true,
        conflictingReservation: conflict.conflictingReservation
      };
    }
    return { hasConflict: false };
  } catch (error) {
    console.error('Error checking reservation conflict:', error);
    throw error;
  }
};

// Add reservation with conflict checking
export const addReservationWithConflictCheck = async (reservationData) => {
  try {
    const {
      date,
      startTime,
      duration,
      numberOfPeople,
      racketsNeeded,
      clientName,
      clientPhone,
      clientEmail,
      hasCourt,
      location,
      comments,
      courtNo = '',
      courtId = 'court1',
      coachId = DEFAULT_COACH_ID,
      coachName = (getCoachById(DEFAULT_COACH_ID) || {}).name
    } = reservationData;
    const cId = coachId || DEFAULT_COACH_ID;
    const cName = coachName || (getCoachById(cId) || {}).name;

    // First, check for conflicts (same coach, per venue + buffer)
    const conflictCheck = await checkReservationConflict(date, startTime, duration, cId, location);
    if (conflictCheck.hasConflict) {
      throw new Error('TIME_CONFLICT');
    }

    // Calculate end time
    const startDate = new Date(`${date}T${startTime}`);
    const endDate = new Date(startDate.getTime() + duration * 60000);
    const endTime = endDate.toTimeString().slice(0, 5);

    const coachProfile = await getCoachProfile(cId);
    const pricing = coachProfile ? {
      price1Person: coachProfile.price1Person,
      price2People: coachProfile.price2People,
      price3People: coachProfile.price3People,
      price4People: coachProfile.price4People
    } : null;
    const trainingTotal = calculateTrainingTotal(duration, numberOfPeople, pricing);
    const trainingPricePerHour = getTrainingPricePerHourWithPricing(numberOfPeople, pricing);

    let courtPrice = 0;
    if (hasCourt !== 'Taip' && location) {
      courtPrice = await calculateCourtPriceForReservation(location, date, startTime, duration);
    }
    const totalPrice = Math.round((trainingTotal + courtPrice) * 100) / 100;

    const reservation = {
      date,
      startTime,
      endTime,
      duration,
      numberOfPeople,
      courtId,
      trainingPrice: trainingPricePerHour,
      totalPrice,
      ...(courtPrice > 0 && { courtPrice }),
      racketsNeeded,
      clientName,
      clientPhone,
      clientEmail,
      hasCourt,
      location,
      comments,
      courtNo: courtNo || '',
      coachId: cId,
      coachName: cName,
      status: 'confirmed',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.RESERVATIONS), reservation);
    const reservationId = docRef.id;

    // Create email notifications and log entry
    try {
      await createNewReservationEmails(reservation, reservationId);
    } catch (emailError) {
      console.error('Error creating reservation emails:', emailError);
      // Don't throw error here - reservation was created successfully
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding reservation:', error);
    throw error;
  }
};

// Mark time slots as unavailable (for a coach)
export const markTimeSlotsAsUnavailable = async (date, startTime, duration, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const docRef = getAvailableSlotsDocRef(cId, date);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const data = snap.data();
    
    // Calculate which slots to mark as unavailable
    const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
    const endMinutes = startMinutes + duration;
    
    const updatedTimeSlots = data.timeSlots.map(slot => {
      const slotMinutes = parseInt(slot.time.split(':')[0]) * 60 + parseInt(slot.time.split(':')[1]);
      if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
        return { ...slot, availability: AVAILABILITY_STATES.UNAVAILABLE };
      }
      return slot;
    });
    
    await updateDoc(docRef, {
      timeSlots: updatedTimeSlots,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error marking time slots as unavailable:', error);
    throw error;
  }
};

// Get reservations for a specific date
export const getReservations = async (date) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('date', '==', date),
      orderBy('startTime')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting reservations:', error);
    throw error;
  }
};

// Get reservations for a date range for a coach
export const getReservationsForRange = async (startDate, endDate, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('coachId', '==', cId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('status', '!=', 'cancelled'),
      orderBy('date'),
      orderBy('startTime')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error('Error getting reservations for range:', error);
    throw error;
  }
};

// Get reservations for a date range (week view) for a coach
export const getReservationsForWeek = async (startDate, coachId = DEFAULT_COACH_ID) => {
  try {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    return getReservationsForRange(startDate, endDate.toISOString().split('T')[0], coachId);
  } catch (error) {
    console.error('Error getting reservations for week:', error);
    throw error;
  }
};

// Get all reservations (for admin)
export const getAllReservations = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      orderBy('date', 'desc'),
      orderBy('startTime')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting all reservations:', error);
    throw error;
  }
};

// Get all future reservations for a specific user (all coaches)
export const getUserFutureReservations = async (userEmail) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('clientEmail', '==', userEmail),
      where('date', '>=', today),
      where('status', '!=', 'cancelled'),
      orderBy('date', 'asc'),
      orderBy('startTime')
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error('Error getting user future reservations:', error);
    throw error;
  }
};

// Get past reservations for a specific user (date < today, any status)
export const getUserPastReservations = async (userEmail) => {
  try {
    const today = new Date().toISOString().split('T')[0];
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('clientEmail', '==', userEmail),
      where('date', '<', today),
      orderBy('date', 'desc'),
      orderBy('startTime', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data()
    }));
  } catch (error) {
    console.error('Error getting user past reservations:', error);
    throw error;
  }
};

/** Court booked with reservation vs coach-assigned meeting court (client pays separately). */
export const formatReservationCourtLabel = (reservation) => {
  const courtNo = (reservation.courtNo || reservation.sebCourtName || '').toString().trim();
  if (reservation.hasCourt === 'Taip') {
    return courtNo ? `Taip, Nr. ${courtNo}` : 'Taip';
  }
  if (courtNo) return `Ne (susitikimas Nr. ${courtNo})`;
  return 'Ne';
};

// Update reservation
export const updateReservation = async (reservationId, updates) => {
  try {
    // Check if status is being changed to 'cancelled'
    if (updates.status === 'cancelled') {
      // Get the current reservation data before updating
      const reservationDoc = await getDoc(doc(db, COLLECTIONS.RESERVATIONS, reservationId));
      if (!reservationDoc.exists()) {
        throw new Error('Reservation not found');
      }
      
      const reservation = {
        id: reservationDoc.id,
        ...reservationDoc.data()
      };
      
      // Update the reservation status and stop cron from searching for a court (if it was still pending)
      const docRef = doc(db, COLLECTIONS.RESERVATIONS, reservationId);
      await updateDoc(docRef, {
        ...updates,
        sebAutoBookPending: false,
        updatedAt: Timestamp.now()
      });
      
      // Update reservation log to cancelled (use setDoc merge so it works even if log doc was never created)
      try {
        await setDoc(doc(db, COLLECTIONS.RESERVATION_LOG, reservationId), { status: 'cancelled' }, { merge: true });
      } catch (logErr) {
        console.error('Failed to update reservation log status:', logErr);
      }
      // Send cancellation emails via Firestore mail collection (admin/coach cancelled)
      createCancellationEmails(reservation, { cancelledByClient: false }).catch((emailError) => {
        console.error('Error sending cancellation emails:', emailError);
      });
    } else {
      const docRef = doc(db, COLLECTIONS.RESERVATIONS, reservationId);
      const reservationDoc = await getDoc(docRef);
      const before = reservationDoc.exists() ? reservationDoc.data() : {};
      const mergedUpdates = { ...updates, updatedAt: Timestamp.now() };

      if (updates.courtNo !== undefined) {
        const trimmedCourt = (updates.courtNo || '').trim();
        mergedUpdates.courtNo = trimmedCourt;
        mergedUpdates.sebCourtName = trimmedCourt;
        // Coach assigning a meeting court must not change billing type or stored price.
        if (before.hasCourt !== 'Taip') {
          delete mergedUpdates.hasCourt;
          delete mergedUpdates.totalPrice;
          delete mergedUpdates.courtPrice;
          delete mergedUpdates.trainingTotal;
        }
      }

      await updateDoc(docRef, mergedUpdates);
    }
  } catch (error) {
    console.error('Error updating reservation:', error);
    throw error;
  }
};

// Delete reservation
export const deleteReservation = async (reservationId) => {
  try {
    const docRef = doc(db, COLLECTIONS.RESERVATIONS, reservationId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const coachId = data.coachId || DEFAULT_COACH_ID;
      await markTimeSlotsAsAvailable(data.date, data.startTime, data.duration, coachId);
      await deleteDoc(docRef);
    }
  } catch (error) {
    console.error('Error deleting reservation:', error);
    throw error;
  }
};

// Cancel reservation (mark as cancelled instead of deleting)
export const cancelReservation = async (reservationId) => {
  try {
    // First get the reservation data to create cancellation emails
    const reservationDoc = await getDoc(doc(db, COLLECTIONS.RESERVATIONS, reservationId));
    if (!reservationDoc.exists()) {
      throw new Error('Reservation not found');
    }
    
    const reservation = {
      id: reservationDoc.id,
      ...reservationDoc.data()
    };
    
    // Update the reservation status and stop cron from searching for a court (if it was still pending)
    await updateDoc(doc(db, COLLECTIONS.RESERVATIONS, reservationId), {
      status: 'cancelled',
      sebAutoBookPending: false,
      updatedAt: Timestamp.now()
    });

    // If this reservation had a court booked on SEB Arena, cancel it there too
    const sebId = reservation.sebReservationId;
    if (sebId) {
      const cancelApiUrl = getEmailApiUrl().replace('/send-email.php', '/cancel-seb.php');
      const apiKey = getEmailApiKey();
      if (cancelApiUrl && apiKey) {
        fetch(cancelApiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Email-API-Key': apiKey },
          body: JSON.stringify({ sebReservationId: sebId })
        }).catch((err) => console.warn('SEB cancel request failed:', err));
      }
    }
    // Update reservation log to cancelled (use setDoc merge so it works even if log doc was never created)
    try {
      await setDoc(doc(db, COLLECTIONS.RESERVATION_LOG, reservationId), { status: 'cancelled' }, { merge: true });
    } catch (logErr) {
      console.error('Failed to update reservation log status:', logErr);
    }
    // Send cancellation emails via Firestore mail collection (client cancelled)
    createCancellationEmails(reservation, { cancelledByClient: true }).catch((emailError) => {
      console.error('Error sending cancellation emails:', emailError);
    });

    return { success: true, reservationId };
  } catch (error) {
    console.error('Error cancelling reservation:', error);
    throw error;
  }
};

// Mark time slots as available (for a coach)
export const markTimeSlotsAsAvailable = async (date, startTime, duration, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const docRef = getAvailableSlotsDocRef(cId, date);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const data = snap.data();
    
    // Calculate which slots to mark as available
    const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
    const endMinutes = startMinutes + duration;
    
    const updatedTimeSlots = data.timeSlots.map(slot => {
      const slotMinutes = parseInt(slot.time.split(':')[0]) * 60 + parseInt(slot.time.split(':')[1]);
      if (slotMinutes >= startMinutes && slotMinutes < endMinutes) {
        return { ...slot, availability: AVAILABILITY_STATES.SEB_ARENA };
      }
      return slot;
    });
    
    await updateDoc(docRef, {
      timeSlots: updatedTimeSlots,
      updatedAt: Timestamp.now()
    });
  } catch (error) {
    console.error('Error marking time slots as available:', error);
    throw error;
  }
};

// Check if a time slot is available (any venue, or a specific venue when location is provided)
export const isSlotAvailable = async (date, startTime, duration, venueId = null) => {
  try {
    const availableSlots = await getAvailableSlots(date);
    if (!availableSlots) return false;

    const startMinutes = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
    const endMinutes = startMinutes + duration;

    for (let minute = startMinutes; minute < endMinutes; minute += 30) {
      const time = `${Math.floor(minute / 60).toString().padStart(2, '0')}:${(minute % 60).toString().padStart(2, '0')}`;
      const slot = availableSlots.timeSlots.find((s) => s.time === time);
      if (!slot) return false;
      if (venueId) {
        if (!getVenuesAvailableInSlot(slot, [venueId]).includes(venueId)) return false;
      } else if (!isSlotBookable(slot)) {
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error checking slot availability:', error);
    throw error;
  }
};

// Get week dates for calendar
export const getWeekDates = (startDate) => {
  const dates = [];
  const start = new Date(startDate);
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push(date.toISOString().split('T')[0]);
  }
  
  return dates;
};

// Court prices (aikštelių kainos): locationId, validFrom, validTo, slots { "dayIndex_time": price }
const courtPricesDocId = (locationId, validFrom, validTo) =>
  `${(locationId || 'seb_arena').replace(/\s/g, '_')}_${validFrom}_${validTo}`;

export const saveCourtPrices = async (locationId, validFrom, validTo, slots) => {
  const id = courtPricesDocId(locationId, validFrom, validTo);
  const ref = doc(db, COLLECTIONS.COURT_PRICES, id);
  await setDoc(ref, {
    locationId: locationId || 'seb_arena',
    validFrom,
    validTo,
    slots: slots || {},
    updatedAt: Timestamp.now()
  });
};

export const getCourtPrices = async (locationId, validFrom, validTo) => {
  const id = courtPricesDocId(locationId, validFrom, validTo);
  const snap = await getDoc(doc(db, COLLECTIONS.COURT_PRICES, id));
  if (!snap.exists()) return { slots: {} };
  const data = snap.data();
  return { slots: data.slots || {}, validFrom: data.validFrom, validTo: data.validTo };
};

/** List all court price documents for admin table (id, locationId, validFrom, validTo, updatedAt, slots) */
export const listCourtPrices = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.COURT_PRICES));
  const list = snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      locationId: data.locationId || 'seb_arena',
      validFrom: data.validFrom || '',
      validTo: data.validTo || '',
      updatedAt: data.updatedAt,
      slots: data.slots || {}
    };
  });
  list.sort((a, b) => {
    const ta = a.updatedAt?.toMillis?.() ?? 0;
    const tb = b.updatedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return list;
};

// Map reservation location to court-prices locationId.
// Admin manages: seb_arena, vrm_courts, bernardinai_courts.
// Legacy: pre-existing prices may be stored under 'other' for VRM or 'bernardinu_courts' (typo) for Bernardinai.
export const getCourtPriceLocationId = (location) => {
  if (!location) return 'seb_arena';
  return location;
};

// Find court prices doc that applies on dateStr (validFrom <= dateStr <= validTo); prefer latest validFrom.
// For VRM, falls back to legacy 'other' entries if no 'vrm_courts' entry covers the date.
// For Bernardinai, falls back to legacy 'bernardinu_courts' (old admin typo) if needed.
export const getCourtPricesForDate = async (locationId, dateStr) => {
  const cid = getCourtPriceLocationId(locationId);
  const lookupIds =
    cid === 'vrm_courts'
      ? [cid, 'other']
      : cid === 'bernardinai_courts'
        ? [cid, 'bernardinu_courts']
        : [cid];
  for (const id of lookupIds) {
    const q = query(
      collection(db, COLLECTIONS.COURT_PRICES),
      where('locationId', '==', id),
      where('validFrom', '<=', dateStr),
      where('validTo', '>=', dateStr),
      orderBy('validFrom', 'desc'),
      limit(1)
    );
    const snap = await getDocs(q);
    if (!snap.empty) {
      const data = snap.docs[0].data();
      return { slots: data.slots || {} };
    }
  }
  return null;
};

// Calculate court price for a time range: splits into hour segments and sums (duration in segment / 60) * price per hour
export const calculateCourtPriceForReservation = async (locationId, dateStr, startTime, durationMinutes) => {
  const docData = await getCourtPricesForDate(locationId, dateStr);
  if (!docData || !docData.slots || Object.keys(docData.slots).length === 0) return 0;
  const slots = docData.slots;
  const weekdayIndex = (new Date(dateStr + 'T12:00:00').getDay() + 6) % 7;
  const [h0, m0] = startTime.split(':').map((s) => parseInt(s, 10) || 0);
  const startMinutes = h0 * 60 + m0;
  const endMinutes = startMinutes + durationMinutes;
  let total = 0;
  for (let h = 7; h <= 23; h++) {
    const hourStart = h * 60;
    const hourEnd = (h + 1) * 60;
    const overlap = Math.max(0, Math.min(endMinutes, hourEnd) - Math.max(startMinutes, hourStart));
    if (overlap <= 0) continue;
    const key = `${weekdayIndex}_${h.toString().padStart(2, '0')}:00`;
    const pricePerHour = slots[key];
    if (pricePerHour != null && typeof pricePerHour === 'number') {
      total += (overlap / 60) * pricePerHour;
    }
  }
  return Math.round(total * 100) / 100;
};

// Cycle through availability states (Elvis: unavailable -> Seb -> VRM)
export const getNextAvailabilityState = (currentState) => {
  const states = [AVAILABILITY_STATES.UNAVAILABLE, AVAILABILITY_STATES.SEB_ARENA, AVAILABILITY_STATES.VRM_COURTS];
  const currentIndex = states.indexOf(currentState);
  const nextIndex = (currentIndex + 1) % states.length;
  return states[nextIndex];
};

// For coaches with only Seb Arena (or Elvis – no VRM): one click = toggle unavailable <-> seb_arena
export const getNextAvailabilityStateForCoach = (currentState, coachId) => {
  const coach = getCoachById(coachId);
  const noVrm = (coach && coach.sebOnly) || coachId === 'elvis';
  if (noVrm) {
    return currentState === AVAILABILITY_STATES.SEB_ARENA ? AVAILABILITY_STATES.UNAVAILABLE : AVAILABILITY_STATES.SEB_ARENA;
  }
  return getNextAvailabilityState(currentState);
}; 

export const subscribeToAvailableSlotsForRange = (startDate, endDate, callback, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const q = query(
      getAvailableSlotsDatesRef(cId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date')
    );
    return onSnapshot(q, (querySnapshot) => {
      const slots = {};
      querySnapshot.forEach(docSnap => {
        slots[docSnap.data().date] = docSnap.data();
      });
      callback(slots);
    }, (error) => {
      console.error('Error in available slots subscription:', error);
    });
  } catch (error) {
    console.error('Error setting up available slots subscription:', error);
    throw error;
  }
};

// Real-time listeners for available slots (per coach)
export const subscribeToAvailableSlotsForWeek = (startDate, callback, coachId = DEFAULT_COACH_ID) => {
  try {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    const endStr = endDate.toISOString().split('T')[0];
    return subscribeToAvailableSlotsForRange(startDate, endStr, callback, coachId);
  } catch (error) {
    console.error('Error setting up available slots subscription:', error);
    throw error;
  }
};

/**
 * Migration: copy old flat availableSlots docs (root collection with coachId in doc body) into per-coach subcollections availableSlots/{coachId}/dates/{date}.
 * Call from admin once. Returns { copied, errors }.
 */
export const migrateAvailableSlotsToPerCoachFolders = async () => {
  const results = { copied: 0, errors: [] };
  try {
    const rootRef = collection(db, COLLECTIONS.AVAILABLE_SLOTS);
    const snapshot = await getDocs(rootRef);
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      const coachId = data.coachId;
      const date = data.date;
      if (!coachId || !date || !data.timeSlots) continue;
      try {
        const newRef = getAvailableSlotsDocRef(coachId, date);
        await setDoc(newRef, {
          date,
          timeSlots: data.timeSlots,
          createdAt: data.createdAt || Timestamp.now(),
          updatedAt: data.updatedAt || Timestamp.now()
        });
        results.copied++;
      } catch (err) {
        results.errors.push({ docId: docSnap.id, coachId, date, message: err.message });
      }
    }
    return results;
  } catch (err) {
    results.errors.push({ message: err.message });
    return results;
  }
};

/**
 * Migration: convert legacy single availability field to venues map on all coach date docs.
 */
export const migrateSlotsToMultiVenue = async () => {
  const results = { updated: 0, errors: [] };
  try {
    const coachesSnap = await getDocs(collection(db, COLLECTIONS.AVAILABLE_SLOTS));
    for (const coachDoc of coachesSnap.docs) {
      const coachId = coachDoc.id;
      const datesRef = getAvailableSlotsDatesRef(coachId);
      const datesSnap = await getDocs(datesRef);
      for (const dateDoc of datesSnap.docs) {
        const data = dateDoc.data();
        if (!data.timeSlots) continue;
        try {
          const timeSlots = normalizeTimeSlots(data.timeSlots).map(stripLegacyAvailability);
          await updateDoc(dateDoc.ref, { timeSlots, updatedAt: Timestamp.now() });
          results.updated++;
        } catch (err) {
          results.errors.push({ coachId, date: data.date, message: err.message });
        }
      }
    }
    return results;
  } catch (err) {
    results.errors.push({ message: err.message });
    return results;
  }
};

/**
 * Delete old flat availableSlots documents (those with coachId and date in the body). Run after migration.
 * Returns { deleted, errors }.
 */
export const deleteOldFlatAvailableSlots = async () => {
  const results = { deleted: 0, errors: [] };
  try {
    const rootRef = collection(db, COLLECTIONS.AVAILABLE_SLOTS);
    const snapshot = await getDocs(rootRef);
    for (const docSnap of snapshot.docs) {
      const data = docSnap.data();
      if (data.coachId != null && data.date != null) {
        try {
          await deleteDoc(docSnap.ref);
          results.deleted++;
        } catch (err) {
          results.errors.push({ docId: docSnap.id, message: err.message });
        }
      }
    }
    return results;
  } catch (err) {
    results.errors.push({ message: err.message });
    return results;
  }
};

export const subscribeToReservationsForRange = (startDate, endDate, callback, coachId = DEFAULT_COACH_ID) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('coachId', '==', cId),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      where('status', '!=', 'cancelled'),
      orderBy('date'),
      orderBy('startTime')
    );
    return onSnapshot(q, (querySnapshot) => {
      const reservations = [];
      querySnapshot.forEach(docSnap => {
        reservations.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      callback(reservations);
    }, (error) => {
      console.error('Error in reservations subscription:', error);
    });
  } catch (error) {
    console.error('Error setting up reservations subscription:', error);
    throw error;
  }
};

// Real-time listeners for reservations (per coach)
export const subscribeToReservationsForWeek = (startDate, callback, coachId = DEFAULT_COACH_ID) => {
  try {
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 6);
    return subscribeToReservationsForRange(
      startDate,
      endDate.toISOString().split('T')[0],
      callback,
      coachId
    );
  } catch (error) {
    console.error('Error setting up reservations subscription:', error);
    throw error;
  }
};

// Real-time listener for user reservations
export const subscribeToUserReservations = (userEmail, callback) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('clientEmail', '==', userEmail),
      where('date', '>=', new Date().toISOString().split('T')[0]),
      where('status', '!=', 'cancelled'),
      orderBy('date'),
      orderBy('startTime')
    );
    
    return onSnapshot(q, (querySnapshot) => {
      const reservations = [];
      querySnapshot.forEach(doc => {
        reservations.push({
          id: doc.id,
          ...doc.data()
        });
      });
      callback(reservations);
    }, (error) => {
      console.error('Error in user reservations subscription:', error);
    });
  } catch (error) {
    console.error('Error setting up user reservations subscription:', error);
    throw error;
  }
};

// Real-time listener for all reservations (admin or coach; coachId filters to that coach)
export const subscribeToAllReservations = (callback, coachId = null) => {
  try {
    const q = coachId
      ? query(
          collection(db, COLLECTIONS.RESERVATIONS),
          where('coachId', '==', coachId),
          orderBy('date', 'desc'),
          orderBy('startTime')
        )
      : query(
          collection(db, COLLECTIONS.RESERVATIONS),
          orderBy('date', 'desc'),
          orderBy('startTime')
        );
    
    return onSnapshot(q, (querySnapshot) => {
      const reservations = [];
      querySnapshot.forEach(docSnap => {
        reservations.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      callback(reservations);
    }, (error) => {
      console.error('Error in all reservations subscription:', error);
    });
  } catch (error) {
    console.error('Error setting up all reservations subscription:', error);
    throw error;
  }
}; 

// User profile management
export const createUserProfile = async (userId, profileData) => {
  try {
    const userProfile = {
      ...profileData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    const docRef = await addDoc(collection(db, 'userProfiles'), {
      userId,
      ...userProfile
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (userId) => {
  try {
    const q = query(
      collection(db, 'userProfiles'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    return {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data()
    };
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (userId, updates) => {
  try {
    const q = query(
      collection(db, 'userProfiles'),
      where('userId', '==', userId)
    );
    
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      throw new Error('User profile not found');
    }
    
    const docRef = querySnapshot.docs[0].ref;
    await updateDoc(docRef, {
      ...updates,
      updatedAt: Timestamp.now()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

/** Delete user's profile data from Firestore (for account deletion / GDPR). Returns number of docs deleted. */
export const deleteUserProfileData = async (userId) => {
  try {
    const q = query(
      collection(db, 'userProfiles'),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    let deleted = 0;
    for (const docSnap of snapshot.docs) {
      await deleteDoc(docSnap.ref);
      deleted++;
    }
    return deleted;
  } catch (error) {
    console.error('Error deleting user profile:', error);
    throw error;
  }
};

// NTRP tennis level options (self-rated)
export const NTRP_LEVELS = [
  '1.0', '1.5', '2.0', '2.5', '3.0', '3.5', '4.0', '4.5', '5.0', '5.5', '6.0', '6.5', '7.0'
];

// Convert image to WebP and upload to Firebase Storage; returns download URL
// options: { quality, noCompression } - quality 0-1; noCompression=true uses quality 1.0 (coaches)
export const uploadUserPhoto = async (userId, file, options = {}) => {
  const MAX_WIDTH = 400;
  const MAX_HEIGHT = 400;
  const WEBP_QUALITY = options.noCompression ? 1.0 : (options.quality ?? 0.8);

  const convertToWebp = (blob) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(blob);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > height && width > MAX_WIDTH) {
          height = (height * MAX_WIDTH) / width;
          width = MAX_WIDTH;
        } else if (height > MAX_HEIGHT) {
          width = (width * MAX_HEIGHT) / height;
          height = MAX_HEIGHT;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (b) => resolve(b || blob),
          'image/webp',
          WEBP_QUALITY
        );
      };
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = url;
    });
  };

  const webpBlob = await convertToWebp(file);
  const path = `userPhotos/${userId}/${Date.now()}.webp`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, webpBlob);
  return getDownloadURL(storageRef);
};

// Get or create user profile (ensures email stored for admin list)
export const getOrCreateUserProfile = async (userId, defaults = {}) => {
  let profile = await getUserProfile(userId);
  if (!profile) {
    await createUserProfile(userId, {
      fullName: defaults.fullName || '',
      phoneNumber: defaults.phoneNumber || '',
      email: defaults.email || '',
      ntrpLevel: defaults.ntrpLevel || '',
      ntrpInfo: defaults.ntrpInfo || '',
      photoURL: defaults.photoURL || ''
    });
    profile = await getUserProfile(userId);
  }
  return profile;
};

// Coach registration (hidden form - creates user + registration record for admin approval)
export const createCoachRegistration = async (userId, data) => {
  try {
    const docRef = await addDoc(collection(db, 'coachRegistrations'), {
      userId,
      email: data.email || '',
      fullName: data.fullName || '',
      phoneNumber: data.phoneNumber || '',
      price1Person: data.price1Person ?? '',
      price2People: data.price2People ?? '',
      price3People: data.price3People ?? '',
      price4People: data.price4People ?? '',
      status: 'pending',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    const docId = docRef.id;
    await createCoachRegistrationEmails({ ...data, docId });
    return docId;
  } catch (error) {
    console.error('Error creating coach registration:', error);
    throw error;
  }
};

// Send emails for coach registration via Firestore mail collection (Trigger Email extension)
export const createCoachRegistrationEmails = async (data) => {
  try {
    const { email, fullName, phoneNumber, price1Person, price2People, price3People, price4People, docId } = data;
    const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const name = fullName || '—';
    const mail = email || '—';
    const phone = phoneNumber || '—';
    const pricingText = [
      price1Person != null && price1Person !== '' ? `1 asmuo: ${price1Person} €/val` : null,
      price2People != null && price2People !== '' ? `2 asmenys: ${price2People} €/val` : null,
      price3People != null && price3People !== '' ? `3 asmenys: ${price3People} €/val` : null,
      price4People != null && price4People !== '' ? `4 asmenys: ${price4People} €/val` : null
    ].filter(Boolean).join(', ') || 'Nenurodyta';

    const adminText = `Prisijungė naujas treneris, patvirtinkite jį admin puslapyje.

Vardas: ${name}
El. paštas: ${mail}
Telefonas: ${phone}
Kainos: ${pricingText}
Registracijos ID: ${docId || '—'}`;

    const adminHtml = `<!DOCTYPE html><html><body style="font-family: sans-serif;">
<p>Prisijungė naujas treneris, patvirtinkite jį admin puslapyje.</p>
<p>Vardas: ${escapeHtml(name)}<br/>
El. paštas: ${mail !== '—' ? `<a href="mailto:${escapeHtml(mail)}">${escapeHtml(mail)}</a>` : '—'}<br/>
Telefonas: ${phone !== '—' ? `<a href="tel:${escapeHtml(phone).replace(/\s/g, '')}">${escapeHtml(phone)}</a>` : '—'}<br/>
Kainos: ${escapeHtml(pricingText)}<br/>
Registracijos ID: ${escapeHtml(docId) || '—'}</p>
</body></html>`;

    await sendEmail(INFO_EMAIL, {
      subject: `Naujas treneris: ${name}`,
      text: adminText,
      html: adminHtml
    }, null, fullName || 'coach_registration', EMAIL_FROM_NAME);

    const coachText = `Sveikiname prisijungus prie TenisoUžsiėmimai.lt komandos.

Administratoriui patvirtinus registraciją – galėsite skelbti savo laisvus laikus treniruotėms.

Su pagarba,
TenisoUžsiėmimai.lt`;

    if (email) {
      await sendEmail(email, {
        subject: 'Registracija gauta – TenisoUžsiėmimai.lt',
        text: coachText
      }, null, fullName || 'coach_registration', EMAIL_FROM_NAME);
    }
  } catch (error) {
    console.error('Error creating coach registration emails:', error);
    // Don't throw – registration record already saved
  }
};

export const getCoachRegistrations = async () => {
  try {
    const q = query(
      collection(db, 'coachRegistrations'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting coach registrations:', error);
    return [];
  }
};

export const updateCoachRegistration = async (docId, updates) => {
  try {
    const docRef = doc(db, 'coachRegistrations', docId);
    await updateDoc(docRef, { ...updates, updatedAt: Timestamp.now() });
  } catch (error) {
    console.error('Error updating coach registration:', error);
    throw error;
  }
};

// Approved coaches (Firestore - no code deploy needed when adding new coaches)
export const getApprovedCoachUids = async () => {
  try {
    const docRef = doc(db, 'config', 'approvedCoaches');
    const snap = await getDoc(docRef);
    const data = snap.exists() ? snap.data() : {};
    return data.uids || [];
  } catch (error) {
    console.error('Error getting approved coaches:', error);
    return [];
  }
};

export const addApprovedCoach = async (uid) => {
  try {
    const docRef = doc(db, 'config', 'approvedCoaches');
    const snap = await getDoc(docRef);
    const current = snap.exists() ? (snap.data().uids || []) : [];
    if (current.includes(uid)) return;
    const uids = [...current, uid];
    if (snap.exists()) {
      await updateDoc(docRef, { uids, updatedAt: Timestamp.now() });
    } else {
      await setDoc(docRef, { uids, createdAt: Timestamp.now(), updatedAt: Timestamp.now() });
    }
  } catch (error) {
    console.error('Error adding approved coach:', error);
    throw error;
  }
};

/** Remove a coach's UID from the approved coaches list (self-removal or admin). */
export const removeApprovedCoach = async (uid) => {
  try {
    const docRef = doc(db, 'config', 'approvedCoaches');
    const snap = await getDoc(docRef);
    const current = snap.exists() ? (snap.data().uids || []) : [];
    const uids = current.filter((id) => id !== uid);
    if (uids.length === current.length) return;
    if (snap.exists()) {
      await updateDoc(docRef, { uids, updatedAt: Timestamp.now() });
    }
  } catch (error) {
    console.error('Error removing approved coach:', error);
    throw error;
  }
};

/** Delete the coach document from Firestore coaches list (so they no longer appear on the site). */
export const removeCoachFromFirestoreList = async (coachId) => {
  try {
    const docRef = doc(db, COLLECTIONS.COACHES, coachId);
    await deleteDoc(docRef);
  } catch (error) {
    if (error?.code === 'not-found') return;
    console.error('Error removing coach from Firestore list:', error);
    throw error;
  }
};

/** Delete all availability slot documents for a coach (availableSlots/{coachId}/dates/*). */
export const deleteAllAvailabilitySlotsForCoach = async (coachId) => {
  try {
    const datesRef = getAvailableSlotsDatesRef(coachId);
    const snapshot = await getDocs(datesRef);
    await Promise.all(snapshot.docs.map((d) => deleteDoc(d.ref)));
  } catch (error) {
    console.error('Error deleting availability slots for coach:', error);
    throw error;
  }
};

/** Send coach removal emails: farewell to coach, notification to info@. */
export const sendCoachRemovalEmails = async (coachEmail, coachName) => {
  const name = coachName || 'Treneris';
  const coachSubject = 'TenisoUžsiėmimai.lt – išbraukimas iš trenerių sąrašo';
  const coachText = `Sveiki,

apgailestaujame, kad nusprendėte išeiti iš TenisoUžsiėmimai.lt. Tikimės, kad greitai sugrįšite ir tęsime bendradarbiavimą.

TenisoUžsiėmimai.lt`;
  await createMailDocument(coachEmail, { subject: coachSubject, text: coachText }, null, name, 'TenisoUžsiėmimai.lt');

  const infoSubject = 'Ištrinta trenerio paskyra';
  const infoText = `Ištrinta trenerio paskyra: ${name}`;
  await createMailDocument('info@tenisouzsiemimai.lt', { subject: infoSubject, text: infoText }, null, name, 'TenisoUžsiėmimai.lt');
};

/**
 * All coaches: from Firestore collection (single source for web + mobile). Falls back to COACHES + approved if empty.
 */
export const getActiveCoachesList = async () => {
  const fromFirestore = await getCoachesFromFirestore();
  const coachesSource = fromFirestore.length > 0 ? fromFirestore : COACHES.map((c) => ({ id: c.id, uid: c.uid, name: c.name, sebOnly: c.sebOnly, email: c.email || '', phone: c.phone || '' }));
  const list = [];
  for (const c of coachesSource) {
    const id = c.id;
    const uid = c.uid || c.id;
    const p = await getCoachProfile(id).catch(() => null);
    const hardcoded = COACHES.find((h) => h.id === id);
    const defDesc = c.defaultDescription || hardcoded?.defaultDescription || 'Aprašymas bus įkeltas vėliau.';
    list.push({
      id,
      uid,
      name: c.name || hardcoded?.name || '—',
      email: p?.email ?? c.email ?? hardcoded?.email ?? '',
      phone: p?.phone ?? c.phone ?? hardcoded?.phone ?? '',
      photoURL: p?.photoURL ?? c.photoURL ?? hardcoded?.photoURL ?? null,
      description: (p?.description || '').trim() || defDesc,
      defaultDescription: c.defaultDescription || hardcoded?.defaultDescription || '',
      sebOnly: c.sebOnly === true,
      facebook: p?.facebook ?? c.facebook ?? hardcoded?.facebook ?? '',
      price1Person: p?.price1Person,
      price2People: p?.price2People,
      price3People: p?.price3People,
      price4People: p?.price4People
    });
  }
  if (fromFirestore.length > 0) return sortCoachesByDisplayOrder(list);
  const approvedUids = await getApprovedCoachUids();
  const inSource = new Set(coachesSource.map((x) => x.uid));
  for (const uid of approvedUids) {
    if (inSource.has(uid)) continue;
    const prof = await getUserProfile(uid).catch(() => null);
    const cprof = await getCoachProfile(uid).catch(() => null);
    list.push({
      id: uid,
      uid,
      name: prof?.fullName || cprof?.fullName || '—',
      email: prof?.email ?? cprof?.email ?? '',
      phone: prof?.phoneNumber ?? cprof?.phone ?? '',
      photoURL: cprof?.photoURL ?? prof?.photoURL ?? null,
      description: (cprof?.description || '').trim() || 'Aprašymas bus įkeltas vėliau.',
      defaultDescription: '',
      sebOnly: false,
      facebook: cprof?.facebook ?? '',
      price1Person: cprof?.price1Person,
      price2People: cprof?.price2People,
      price3People: cprof?.price3People,
      price4People: cprof?.price4People
    });
  }
  return sortCoachesByDisplayOrder(list);
};

/** Send welcome email to coach when admin confirms their registration. */
export const sendCoachConfirmationEmail = async (email, fullName) => {
  const addr = (email || '').trim();
  if (!addr || !addr.includes('@')) return;
  const name = (fullName || '').trim() || 'Treneri';
  const subject = 'Sveikiname prisijungus prie TenisoUžsiėmimai.lt';
  const text = `Laba diena, ${name}

Sveikiname prisijungus prie TenisoUžsiėmimai.lt komandos.
Savo nuotrauką, aprašymą ir kainas galite koreaguoti savo profilyje (viršutiniame dešiniajame kampe apvali ikona -> Profilis).

Linkime sėkmingo darbo ir patogaus naudojimosi rezervacijų sistema!
- TenisoUžsiėmimai.lt komanda`;
  await sendEmail(addr, { subject, text }, null, name, EMAIL_FROM_NAME);
};

/** Get unique clients who have made reservations with a coach (for coach clients page). */
export const getClientsForCoach = async (coachId) => {
  try {
    const cId = coachId || DEFAULT_COACH_ID;
    const q = query(
      collection(db, COLLECTIONS.RESERVATIONS),
      where('coachId', '==', cId),
      orderBy('date', 'desc')
    );
    const snapshot = await getDocs(q);
    const byEmail = new Map();
    for (const docSnap of snapshot.docs) {
      const d = docSnap.data();
      if (d.status === 'cancelled') continue;
      const key = (d.clientEmail || '').toLowerCase().trim();
      if (!key) continue;
      if (!byEmail.has(key)) {
        byEmail.set(key, {
          id: key,
          clientName: d.clientName || '',
          clientEmail: d.clientEmail || '',
          clientPhone: d.clientPhone || '',
          reservationCount: 0
        });
      }
      byEmail.get(key).reservationCount += 1;
    }
    return Array.from(byEmail.values()).sort((a, b) =>
      (b.clientName || b.clientEmail || '').localeCompare(a.clientName || a.clientEmail || '')
    );
  } catch (err) {
    console.error('Error getting clients for coach:', err);
    return [];
  }
};

/** Create a client report (coach reports a client) and send email to info@tenisouzsiemimai.lt */
export const createClientReport = async ({ coachId, coachName, coachUid, clientName, clientEmail, clientPhone, reason }) => {
  const reportDoc = {
    coachId: coachId || '',
    coachName: coachName || '',
    coachUid: coachUid || '',
    clientName: clientName || '',
    clientEmail: clientEmail || '',
    clientPhone: clientPhone || '',
    reason: (reason || '').trim(),
    reportedAt: Timestamp.now()
  };
  const docRef = await addDoc(collection(db, COLLECTIONS.CLIENT_REPORTS), reportDoc);

  const adminEmail = 'info@tenisouzsiemimai.lt';
  const subject = `[Raportuota] Treneris pranešė apie klientą – ${clientName || clientEmail || 'N/A'}`;
  const text = `Treneris ${coachName} pranešė apie klientą:

Klientas: ${clientName}
El. paštas: ${clientEmail}
Telefonas: ${clientPhone}

Priežastis: ${reason || '(nenurodyta)'}

---
Šis pranešimas išsiųstas iš TenisoUžsiėmimai.lt rezervacijų sistemos.`;
  const html = `<!DOCTYPE html>
<html><head><meta charset="UTF-8"></head>
<body style="margin:0;padding:24px;font-family:Arial,sans-serif;font-size:16px;line-height:1.5;color:#333;">
  <p style="margin:0 0 16px 0;"><strong>Treneris ${coachName}</strong> pranešė apie klientą:</p>
  <table style="border-collapse:collapse;margin:16px 0;">
    <tr><td style="padding:8px 16px 8px 0;font-weight:600;">Klientas:</td><td>${clientName || '—'}</td></tr>
    <tr><td style="padding:8px 16px 8px 0;font-weight:600;">El. paštas:</td><td>${clientEmail || '—'}</td></tr>
    <tr><td style="padding:8px 16px 8px 0;font-weight:600;">Telefonas:</td><td>${clientPhone || '—'}</td></tr>
  </table>
  <p style="margin:16px 0 0 0;"><strong>Priežastis:</strong></p>
  <p style="margin:8px 0;padding:12px;background:#f7fafc;border-radius:8px;">${(reason || '(nenurodyta)').replace(/\n/g, '<br/>')}</p>
  <p style="margin-top:24px;font-size:12px;color:#718096;">Šis pranešimas išsiųstas iš TenisoUžsiėmimai.lt rezervacijų sistemos.</p>
</body></html>`;

  await createMailDocument(adminEmail, { subject, text, html }, null, clientName, 'TenisoUžsiėmimai.lt');

  return docRef.id;
};

/** Get all reported clients (for admin Raportuoti tab). Returns list with report info. */
export const getReportedClients = async () => {
  try {
    const q = query(
      collection(db, COLLECTIONS.CLIENT_REPORTS),
      orderBy('reportedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    const reports = snapshot.docs.map((d) => ({ id: d.id, ...d.data(), reportedAt: d.data().reportedAt?.toDate?.() }));
    const byEmail = new Map();
    for (const r of reports) {
      const key = (r.clientEmail || '').toLowerCase().trim();
      if (!key) continue;
      if (!byEmail.has(key)) {
        byEmail.set(key, {
          id: r.id,
          clientName: r.clientName || '',
          clientEmail: r.clientEmail || '',
          clientPhone: r.clientPhone || '',
          reports: [],
          userId: null
        });
      }
      byEmail.get(key).reports.push({ coachName: r.coachName, reason: r.reason, reportedAt: r.reportedAt });
    }
    const list = Array.from(byEmail.values());
    list.sort((a, b) => {
      const ta = a.reports[0]?.reportedAt?.getTime?.() ?? 0;
      const tb = b.reports[0]?.reportedAt?.getTime?.() ?? 0;
      return tb - ta;
    });
    return list;
  } catch (err) {
    console.error('Error getting reported clients:', err);
    return [];
  }
};

/** Get user profile by email (for admin reported clients - to link to userProfiles for edit/block). */
export const getUserProfileByEmail = async (email) => {
  if (!email || !email.trim()) return null;
  const trimmed = email.trim().toLowerCase();
  const q = query(
    collection(db, 'userProfiles'),
    where('email', '==', trimmed)
  );
  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;
  const d = snapshot.docs[0];
  return { id: d.id, userId: d.data().userId, ...d.data() };
};

// Get all user profiles (admin)
export const getAllUserProfiles = async () => {
  const snapshot = await getDocs(collection(db, 'userProfiles'));
  const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  list.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? a.updatedAt?.toMillis?.() ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? b.updatedAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
  return list;
};

// Sender display name for emails (inline server SMTP)
const EMAIL_FROM_NAME = 'TenisoUžsiėmimai.lt';

// CRA replaces process.env.REACT_APP_* at build time – use exact pattern, no optional chaining
const getEmailApiUrl = () =>
  process.env.REACT_APP_EMAIL_API_URL ||
  (typeof window !== 'undefined' && window.location?.origin
    ? `${window.location.origin}/auto-book/api/send-email.php`
    : '');
const getEmailApiKey = () => process.env.REACT_APP_EMAIL_API_KEY || '';

const COACH_REGISTRATION_LINK = 'https://tenisouzsiemimai.lt/#/coachregistrationform';

/** Get all coach invites (from coaches invite form, norintys) - newest first. */
export const getCoachInvites = async () => {
  try {
    const q = query(
      collection(db, 'coachInvites'),
      orderBy('submittedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({ id: d.id, ...d.data(), submittedAt: d.data().submittedAt?.toDate?.() || null }));
  } catch (error) {
    console.error('Error getting coach invites:', error);
    return [];
  }
};

/** Delete a coach invite (norintys) entry. */
export const deleteCoachInvite = async (inviteId) => {
  await deleteDoc(doc(db, 'coachInvites', inviteId));
};

/** Send invite link email to a coach who filled the invite form (norintys entry). */
export const sendCoachInviteLinkEmail = async (invite) => {
  const email = (invite.email || '').trim();
  if (!email) {
    throw new Error('El. paštas privalomas');
  }
  const fullName = [invite.firstName, invite.lastName].filter(Boolean).join(' ').trim() || 'Treneris';
  const subject = 'Kviečiame prisijungti prie TenisoUžsiėmimai.lt';
  const text = `Kviečiame prisijungti prie TenisoUžsiėmimai.lt

Registracijos nuoroda: ${COACH_REGISTRATION_LINK}

Su pagarba,
TenisoUžsiėmimai.lt`;
  const html = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:24px;font-family:Arial,sans-serif;font-size:16px;line-height:1.5;color:#333;">
  <p style="margin:0 0 24px 0;">Kviečiame prisijungti prie TenisoUžsiėmimai.lt</p>
  <table cellpadding="0" cellspacing="0" border="0" style="margin:28px 0;"><tr><td style="padding:14px 32px;background:#48bb78;border-radius:8px;border:2px solid #38a169;">
    <a href="${COACH_REGISTRATION_LINK}" style="color:#fff !important;text-decoration:none;font-weight:700;font-size:16px;">Registruotis</a>
  </td></tr></table>
  <p style="font-size:12px;color:#718096;margin:16px 0;">Jei mygtukas neveikia - spauskite nuorodą: ${COACH_REGISTRATION_LINK}</p>
  <p style="margin-top:28px;">Su pagarba,<br/>TenisoUžsiėmimai.lt</p>
</body>
</html>`;
  await sendEmail(email, { subject, text, html }, null, fullName, EMAIL_FROM_NAME);
};

// Coach email and contact map (for coach-specific info in emails)
const COACH_EMAILS = {
  elvis: 'gotovskiselvis@gmail.com',
  julija: 'julija.baltuskiene@gmail.com',
  vitalijus: 'vitalijus.duda@gmail.com'
};
const COACH_CONTACTS = {
  elvis: { phone: '+37069614152', email: 'gotovskiselvis@gmail.com' },
  julija: { phone: '+37060338403', email: 'julija.baltuskiene@gmail.com' },
  vitalijus: { email: 'vitalijus.duda@gmail.com', phone: null }
};
const getCoachContact = (coachId) => COACH_CONTACTS[coachId] || null;

const getDayName = (dateStr) => {
  try {
    const d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('lt-LT', { weekday: 'long' });
  } catch (_) {
    return '';
  }
};

const getVenueEmailDisplay = (locationId) => {
  const name = getLocationDisplayName(locationId);
  const address = LOCATION_INFO[locationId]?.address;
  return address ? `${name} – ${address}` : name;
};

/** Build Google Calendar add URL (times interpreted as Europe/Vilnius). */
function buildCalendarUrl(dateStr, startTime, duration, title, details) {
  const date = dateStr || '';
  const time = (startTime || '').slice(0, 5);
  const dur = parseInt(duration, 10) || 60;
  try {
    const datesParam = vilniusToCalendarUTC(date, time, dur);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&details=${encodeURIComponent(details)}&dates=${datesParam}`;
  } catch (_) {
    return '';
  }
}

/** Build and send reservation created emails via Firestore mail collection. */
async function sendReservationCreatedEmailsViaFirebase(reservation, reservationId) {
  const str = (key, def = '') => {
    const v = reservation[key];
    return v != null ? String(v) : def;
  };
  const locationName = getLocationDisplayName(reservation.location);
  const venueDisplay = getVenueEmailDisplay(reservation.location);
  const coachName = str('coachName', 'Elviu');
  const courtNo = (str('courtNo') || str('sebCourtName') || '').trim();
  const courtTitlePart = courtNo ? ` – Aikštelė - ${courtNo}` : '';
  const coachId = str('coachId', 'elvis');
  const duration = str('duration', '60');
  const commentsLine = (str('comments') || '').trim();

  // Contact info: from COACH_CONTACTS for known coaches, else from COACHES + userProfiles (by coach UID)
  let coachContact = getCoachContact(coachId);
  if (!coachContact) {
    const coach = getCoachById(coachId);
    const uid = coach?.uid;
    const profile = uid ? await getUserProfile(uid).catch(() => null) : null;
    if (profile?.email) {
      coachContact = { email: profile.email, phone: profile.phoneNumber || null };
    } else if (coach?.email) {
      coachContact = { email: coach.email, phone: coach.phone || null };
    } else {
      coachContact = COACH_CONTACTS.elvis;
    }
  }
  const coachPhone = coachContact.phone || '+37069614152';
  const coachPhoneTelHref = (coachPhone || '').replace(/\s/g, '');
  const coachPhoneEscaped = (coachPhone || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const coachEmailAddr = coachContact.email || 'gotovskiselvis@gmail.com';
  const coachRecipientEmail = COACH_EMAILS[coachId] || coachEmailAddr;

  const clientEmailAddr = str('clientEmail');
  const rackets = (reservation.racketsNeeded) ? 'Taip' : 'Ne';
  const courtMessage = str('hasCourt') === 'Taip'
    ? (courtNo ? `Susitiksime aikštelėje – aikštelė Nr. ${courtNo}.` : 'Susitiksime aikštelėje!')
    : 'Rezervavote treniruotę be aikštelės. Aikštelės numerį treneris informuos greitu metu.';
  const clientCourtLine = courtNo ? `\n🏟️ Aikštelė: ${courtNo}` : '';
  const clientCommentsBlock = commentsLine ? `\n\n📝 Komentarai: ${commentsLine}` : '';
  const dateWithDay = str('date') + (getDayName(str('date')) ? ` (${getDayName(str('date'))})` : '');
  const priceDisplay = reservation.totalPrice != null ? `${Number(reservation.totalPrice).toFixed(2)}` : '—';
  const priceLine = `Kaina: ${priceDisplay} Eur`;

  // Client confirmation (no Raketės section)
  if (clientEmailAddr && !clientEmailAddr.includes('anonymous')) {
    const eventTitleClient = `Tenisas su ${coachName} ${locationName}${courtTitlePart}`;
    const eventDetailsClient = `Telefonas: ${coachPhone}\nEl. Paštas: ${coachEmailAddr}${clientCourtLine}${commentsLine ? '\nKomentarai: ' + commentsLine : ''}`;
    const calendarClient = buildCalendarUrl(reservation.date, reservation.startTime, duration, eventTitleClient, eventDetailsClient);
    const subject = `rezervacija ${str('date')} ${str('startTime')} su treneriu ${coachName}${courtTitlePart}`;
    const text = `Sveiki ${str('clientName')}!\n\nDėkojame, kad rezervavote teniso treniruotę su ${coachName}!\n\n📅 Data: ${dateWithDay}\n🕐 Laikas: ${str('startTime')}\n📍 Vieta: ${locationName}${clientCourtLine}\n⏱️ Trukmė: ${duration} min\n👥 Žmonių skaičius: ${str('numberOfPeople', '1')}\n${priceLine}${clientCommentsBlock}\n\n${courtMessage}\n\n📅 Pridėti prie kalendoriaus: ${calendarClient}\n\nKontaktinė informacija (${coachName}):\n📞 Telefonas: ${coachPhone}\n📧 El. paštas: ${coachEmailAddr}\n\nDėkojame, kad naudojatės tenisouzsiemimai.lt rezervacijos sistema.\n\nPagarbiai,\nTenisoUžsiėmimai.lt komanda`;
    const clientHtml = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:24px;font-family:Arial,sans-serif;font-size:16px;line-height:1.5;color:#333;">
  <p style="margin:0 0 24px 0;">Sveiki ${str('clientName')}!</p>
  <p style="margin:0 0 24px 0;">Dėkojame, kad rezervavote teniso treniruotę su ${coachName}!</p>
  <p style="margin:0 0 12px 0;">📅 Data: ${dateWithDay}<br/>🕐 Laikas: ${str('startTime')}<br/>📍 Vieta: ${locationName}${clientCourtLine ? '<br/>🏟️ Aikštelė: ' + courtNo : ''}<br/>⏱️ Trukmė: ${duration} min<br/>👥 Žmonių skaičius: ${str('numberOfPeople', '1')}<br/>${priceLine}${clientCommentsBlock ? '<br/><br/>📝 Komentarai: ' + commentsLine : ''}</p>
  <p style="margin:0 0 24px 0;">${courtMessage}</p>
  <table cellpadding="0" cellspacing="0" border="0" style="margin:28px 0;"><tr><td style="padding:14px 32px;background:#1976d2;border-radius:8px;border:2px solid #1565c0;">
    <a href="${calendarClient}" style="color:#fff !important;text-decoration:none;font-weight:700;font-size:16px;">📅 Pridėti į kalendorių</a>
  </td></tr></table>
  <p style="font-size:12px;color:#718096;margin:16px 0;">Jei mygtukas neveikia - spauskite nuorodą: <a href="${calendarClient}">${calendarClient}</a></p>
  <p style="margin:24px 0 0 0;">Kontaktinė informacija (${coachName}):<br/>📞 Telefonas: <a href="tel:${coachPhoneTelHref}" style="color:#1976d2;text-decoration:underline;">${coachPhoneEscaped}</a><br/>📧 El. paštas: ${coachEmailAddr}</p>
  <p style="margin-top:28px;">Dėkojame, kad naudojatės <a href="https://tenisouzsiemimai.lt">tenisouzsiemimai.lt</a> rezervacijos sistema.</p>
  <p style="margin-top:16px;">Pagarbiai,<br/>TenisoUžsiėmimai.lt komanda</p>
</body>
</html>`;
    await createMailDocument(clientEmailAddr, { subject, text, html: clientHtml }, reservationId, str('clientName'), coachName);
  }

  // Coach confirmation
  const eventTitleCoach = str('clientName');
  const courtDisplay = str('hasCourt') === 'Taip' && courtNo ? `Taip, Nr. ${courtNo}` : str('hasCourt', 'Ne');
  const courtCoachLine = `Aikštelė: ${courtDisplay}`;
  const eventDetailsCoach = `${courtCoachLine}\nTelefonas: ${str('clientPhone')}\nEl. paštas: ${str('clientEmail')}\nVieta: ${venueDisplay}\nTrukmė: ${duration} min\nŽmonių skaičius: ${str('numberOfPeople', '1')}\nAr reikės rakečių: ${rackets}\n${priceLine}\n\nKomentarai: ${str('comments') || 'Nėra komentarų'}`;
  const calendarCoach = buildCalendarUrl(reservation.date, reservation.startTime, duration, eventTitleCoach, eventDetailsCoach);
  const coachCommentsBlock = commentsLine ? `\nKomentarai: ${commentsLine}` : '';
  const coachSubject = `Nauja rezervacija – ${str('clientName')} ${str('date')} ${str('startTime')} – ${locationName}${courtTitlePart}`;
  const coachText = `Sveiki!\n\nJums skirta nauja rezervacija.\n\nKlientas: ${str('clientName')}\nTelefonas: ${str('clientPhone')}\nEl. paštas: ${str('clientEmail')}\nData: ${dateWithDay}\nLaikas: ${str('startTime')}\n📍 Vieta: ${venueDisplay}\nTrukmė: ${duration} min\nŽmonių skaičius: ${str('numberOfPeople', '1')}\nAikštelė: ${courtDisplay}\nAr reikės rakečių: ${rackets}\n${priceLine}${coachCommentsBlock}\n\n📅 Pridėti prie kalendoriaus: ${calendarCoach}`;
  const coachHtml = `<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:24px;font-family:Arial,sans-serif;font-size:16px;line-height:1.5;color:#333;">
  <p style="margin:0 0 24px 0;">Sveiki!</p>
  <p style="margin:0 0 24px 0;">Jums skirta nauja rezervacija.</p>
  <p style="margin:0 0 12px 0;">Klientas: ${str('clientName')}<br/>Telefonas: ${str('clientPhone')}<br/>El. paštas: ${str('clientEmail')}<br/>Data: ${dateWithDay}<br/>Laikas: ${str('startTime')}<br/>📍 Vieta: ${venueDisplay}<br/>Trukmė: ${duration} min<br/>Žmonių skaičius: ${str('numberOfPeople', '1')}<br/>Aikštelė: ${courtDisplay}<br/>Ar reikės rakečių: ${rackets}<br/>${priceLine}${coachCommentsBlock ? '<br/>Komentarai: ' + commentsLine : ''}</p>
  <table cellpadding="0" cellspacing="0" border="0" style="margin:28px 0;"><tr><td style="padding:14px 32px;background:#1976d2;border-radius:8px;border:2px solid #1565c0;">
    <a href="${calendarCoach}" style="color:#fff !important;text-decoration:none;font-weight:700;font-size:16px;">📅 Pridėti į kalendorių</a>
  </td></tr></table>
  <p style="font-size:12px;color:#718096;margin:16px 0;">Jei mygtukas neveikia - spauskite nuorodą: <a href="${calendarCoach}">${calendarCoach}</a></p>
</body>
</html>`;
  await createMailDocument(coachRecipientEmail, { subject: coachSubject, text: coachText, html: coachHtml }, reservationId, str('clientName'), EMAIL_FROM_NAME);
}

/** Build and send cancellation emails via Firestore mail collection. */
async function sendCancellationEmailsViaFirebase(reservation, cancelledByClient) {
  const str = (key, def = '') => {
    const v = reservation[key];
    return v != null ? String(v) : def;
  };
  const locationName = getLocationDisplayName(reservation.location);
  const venueDisplay = getVenueEmailDisplay(reservation.location);
  const coachName = str('coachName', 'Elviu');
  const coachId = str('coachId', 'elvis');
  let coachContact = getCoachContact(coachId);
  if (!coachContact) {
    const coach = getCoachById(coachId);
    const uid = coach?.uid;
    const profile = uid ? await getUserProfile(uid).catch(() => null) : null;
    if (profile?.email) {
      coachContact = { email: profile.email, phone: profile.phoneNumber || null };
    } else if (coach?.email) {
      coachContact = { email: coach.email, phone: coach.phone || null };
    } else {
      coachContact = COACH_CONTACTS.elvis;
    }
  }
  const coachPhone = coachContact.phone || '+37069614152';
  const coachEmailAddr = coachContact.email || 'gotovskiselvis@gmail.com';
  const coachRecipientEmail = COACH_EMAILS[coachId] || coachEmailAddr;
  const hadSebCourt = !!(reservation.sebReservationId || reservation.sebCourtBooked);

  // Client email
  const clientEmailAddr = str('clientEmail');
  if (clientEmailAddr && !clientEmailAddr.includes('anonymous')) {
    const dateWithDay = str('date') + (getDayName(str('date')) ? ` (${getDayName(str('date'))})` : '');
    let subject, text;
    if (cancelledByClient) {
      subject = `Rezervacija atšaukta – ${str('date')} ${str('startTime')} su ${coachName}`;
      text = `Sveiki ${str('clientName')}!\n\nJūs atšaukėte treniruotę su ${coachName}. Maloniai lauksime kitais kartais.\n\n📅 Data: ${dateWithDay}\n🕐 Laikas: ${str('startTime')}\n📍 Vieta: ${locationName}\n\nSu pagarba,\nTenisoUžsiėmimai.lt`;
    } else {
      subject = `rezervacija ${str('date')} ${str('startTime')} su treneriu ${coachName}`;
      text = `Sveiki ${str('clientName')}!\n\nJūsų teniso treniruotė su ${coachName} buvo atšaukta.\n\n📅 Data: ${dateWithDay}\n🕐 Laikas: ${str('startTime')}\n📍 Vieta: ${locationName}\n\nJei turite klausimų dėl atšaukimo, susisiekite su mumis:\n\n📞 Telefonas: ${coachPhone}\n📧 El. paštas: ${coachEmailAddr}\n\nDėkojame už supratimą!\n\nSu pagarba,\n${coachName}\nTeniso treneris`;
    }
    await createMailDocument(clientEmailAddr, { subject, text }, reservation.id, str('clientName'), EMAIL_FROM_NAME);
  }

  // Coach: lesson cancelled
  const subject = `Rezervacija atšaukta – ${str('clientName')} ${str('date')} ${str('startTime')}`;
  const coachText = cancelledByClient
    ? `Klientas atšaukė rezervaciją.\n\nKlientas: ${str('clientName')}\nTelefonas: ${str('clientPhone')}\nEl. paštas: ${str('clientEmail')}\nData: ${str('date')}\nLaikas: ${str('startTime')}\n📍 Vieta: ${venueDisplay}`
    : `Rezervacija atšaukta.\n\nKlientas: ${str('clientName')}\nTelefonas: ${str('clientPhone')}\nEl. paštas: ${str('clientEmail')}\nData: ${str('date')}\nLaikas: ${str('startTime')}\n📍 Vieta: ${venueDisplay}\n\nŠi rezervacija nebegalioja.`;
  await createMailDocument(coachRecipientEmail, { subject, text: coachText }, reservation.id, str('clientName'), EMAIL_FROM_NAME);

  // Coach: court cancelled (SEB) when there was a court
  if (hadSebCourt) {
    const courtText = `Seb arena aikštelė - atšaukta. ${str('clientName')} ${str('date')} ${str('startTime')}\n\nKlientas: ${str('clientName')}\nTelefonas: ${str('clientPhone')}\nEl. paštas: ${str('clientEmail')}\nData: ${str('date')}\nLaikas: ${str('startTime')}\n📍 Vieta: ${venueDisplay}\n\nŠi rezervacija nebegalioja.`;
    await createMailDocument(coachRecipientEmail, { subject, text: courtText }, reservation.id, str('clientName'), 'SEB-Autobook');
  }
}

/** Send coach invite form emails via Firestore mail collection (Trigger Email extension). */
export async function callCoachInviteEmailApi(coachInvite) {
  const firstName = (coachInvite.firstName || '').trim();
  const lastName = (coachInvite.lastName || '').trim();
  const email = (coachInvite.email || '').trim();
  const phone = (coachInvite.phone || '').trim();
  const name = [firstName, lastName].filter(Boolean).join(' ');
  const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

  // To info@: notification about new coach wanting to join
  const infoSubject = 'Naujas treneris norintis prisijungti prie sistemos';
  const infoText = `Naujas treneris norintis prisijungti prie sistemos:\n\n${name}\nEl. paštas: ${email}\nTelefonas: ${phone}`;
  const infoHtml = `Naujas treneris norintis prisijungti prie sistemos:<br><br>${escapeHtml(name)}<br>El. paštas: <a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a><br>Telefonas: <a href="tel:${escapeHtml(phone).replace(/\s/g, '')}">${escapeHtml(phone)}</a>`;
  await sendEmail(INFO_EMAIL, { subject: infoSubject, text: infoText, html: infoHtml }, null, name, EMAIL_FROM_NAME);

  // To submitter: confirmation
  if (email) {
    const confirmSubject = 'Jūsų pageidavimą prisijungti - gavome';
    const confirmText = 'Laba diena,\n\nJūsų pageidavimą prisijungti prie TenisoUžsiėmimai.lt - gavome.\nGreitu metu atsiūsime informaciją prisijungimui.\n\nSu pagarba,\nTenisoUžsiėmimai.lt';
    await sendEmail(email, { subject: confirmSubject, text: confirmText }, null, name, EMAIL_FROM_NAME);
  }
}

/** Send one email via Firestore mail collection (fast). SMTP API code commented out for possible future use. */
async function sendEmail(to, message, reservationId = null, clientName = null, fromName = null) {
  // SMTP path (commented out – uncomment to switch back to PHP send-email.php):
  // const apiUrl = getEmailApiUrl();
  // const apiKey = getEmailApiKey();
  // if (apiUrl && apiKey) {
  //   try {
  //     const res = await fetch(apiUrl, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json', 'X-Email-API-Key': apiKey },
  //       body: JSON.stringify({
  //         to,
  //         subject: message.subject,
  //         text: message.text,
  //         ...(message.html && { html: message.html }),
  //         fromName: fromName || EMAIL_FROM_NAME
  //       })
  //     });
  //     const data = await res.json().catch(() => ({}));
  //     if (res.ok && data.ok) return;
  //     console.warn('Email API returned non-ok:', data);
  //   } catch (err) {
  //     console.warn('Email API request failed, falling back to Firestore:', err);
  //   }
  // }
  await createMailDocument(to, message, reservationId, clientName, fromName);
}

// Mail collection – emails are written here; processed by Firebase Trigger Email extension or similar
export const createMailDocument = async (to, message, reservationId = null, clientName = null, fromName = null) => {
  try {
    const mailDoc = {
      to: to,
      message: {
        subject: message.subject,
        text: message.text,
        ...(message.html && { html: message.html }),
        ...(Array.isArray(message.attachments) && message.attachments.length > 0
          ? { attachments: message.attachments }
          : {})
      },
      createdAt: Timestamp.now(),
      status: 'pending',
      clientName: clientName || 'unknown',
      reservationId: reservationId || null,
      createdDate: new Date().toISOString(),
      ...(fromName && { fromName })
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.MAIL), mailDoc);
    return docRef.id;
  } catch (error) {
    console.error('Error creating mail document:', error);
    throw error;
  }
};

// Create email for new reservation: log to reservationLog, then backend sends emails (reservation-email.php)
export const createNewReservationEmails = async (reservation, reservationId) => {
  try {
    // Log to reservationLog for admin view (all reservations in one place)
    if (reservationId) {
      try {
        await setDoc(doc(db, COLLECTIONS.RESERVATION_LOG, reservationId), {
          createdAt: Timestamp.now(),
          clientName: reservation.clientName || '',
          clientEmail: reservation.clientEmail || '',
          clientPhone: reservation.clientPhone || '',
          coachId: reservation.coachId || DEFAULT_COACH_ID,
          coachName: reservation.coachName || 'Elvis Gotovskis',
          lessonDate: reservation.date,
          lessonStartTime: reservation.startTime || '',
          duration: reservation.duration || 60,
          comments: reservation.comments || '',
          hasCourt: reservation.hasCourt || 'Ne',
          courtNo: reservation.courtNo || reservation.sebCourtName || '',
          reservationId,
          status: 'confirmed'
        });
      } catch (logError) {
        console.error('Error writing reservation log:', logError);
      }
    }
    // Send via Firebase mail collection (fast).
    await sendReservationCreatedEmailsViaFirebase(reservation, reservationId);
  } catch (error) {
    console.error('Error creating reservation emails:', error);
    throw error;
  }
};

// Create email for cancelled reservation: backend (reservation-email.php) sends; optionally update log
// options.cancelledByClient: true when client cancelled
export const createCancellationEmails = async (reservation, options = {}) => {
  try {
    if (reservation.id) {
      try {
        await setDoc(doc(db, COLLECTIONS.RESERVATION_LOG, reservation.id), { status: 'cancelled' }, { merge: true });
      } catch (logErr) {
        console.error('Failed to update reservation log status:', logErr);
      }
    }
    // Send via Firebase mail collection (fast).
    await sendCancellationEmailsViaFirebase(reservation, options.cancelledByClient === true);
  } catch (error) {
    console.error('Error creating cancellation emails:', error);
  }
};

// Fetch all reservation log entries for admin (sorted by lesson date, upcoming first)
export const getReservationLog = async () => {
  const q = query(
    collection(db, COLLECTIONS.RESERVATION_LOG),
    orderBy('lessonDate', 'asc'),
    orderBy('lessonStartTime', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.() || null }));
};

// Fetch mail queue entries for admin (newest first), enriched with reservation location.
export const getMailQueueEntries = async () => {
  const q = query(collection(db, COLLECTIONS.MAIL), orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  const entries = snapshot.docs.map((d) => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate?.() || null }));

  const reservationIds = Array.from(
    new Set(
      entries
        .map((entry) => entry.reservationId)
        .filter((reservationId) => typeof reservationId === 'string' && reservationId.trim())
    )
  );

  const reservationLocationById = new Map();
  await Promise.all(
    reservationIds.map(async (reservationId) => {
      try {
        const reservationSnap = await getDoc(doc(db, COLLECTIONS.RESERVATIONS, reservationId));
        if (!reservationSnap.exists()) return;
        const reservation = reservationSnap.data() || {};
        const locationId = reservation.location || '';
        reservationLocationById.set(reservationId, {
          locationId,
          locationName: getLocationDisplayName(locationId)
        });
      } catch (error) {
        console.error(`Failed to fetch reservation ${reservationId} for mail entry`, error);
      }
    })
  );

  return entries.map((entry) => {
    const locationInfo = reservationLocationById.get(entry.reservationId);
    return {
      ...entry,
      locationId: locationInfo?.locationId || '',
      locationName: locationInfo?.locationName || ''
    };
  });
};

// Admin email for notifications (photo upload, etc.)
const ADMIN_EMAIL = 'gotovskiselvis@gmail.com';
// Group training requests and other info emails go here; no reservation copies sent here anymore
const INFO_EMAIL = 'info@tenisouzsiemimai.lt';

// Notify admin when a user uploads a profile photo (admin only, no email to user)
export const notifyAdminPhotoUpload = async ({ fullName, email }) => {
  try {
    const adminText = `Vartotojas įkėlė naują profilio nuotrauką.

Vardas: ${fullName || '—'}
El. paštas: ${email || '—'}`;

    await sendEmail(ADMIN_EMAIL, {
      subject: `Profilio nuotrauka: ${fullName || email || 'Vartotojas'}`,
      text: adminText
    }, null, fullName || 'photo_upload', EMAIL_FROM_NAME);
  } catch (error) {
    console.error('Error creating photo upload notification email:', error);
    // Don't throw – user upload already succeeded
  }
};

// Create emails for group training preference submission
export const createGroupTrainingEmails = async (data) => {
  try {
    const { fullName, phoneNumber, email, ntrpLevel, trainingsPerWeek, groupTrainingPreferences } = data;
    const escapeHtml = (s) => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

    // Group by day: dayName -> [time1, time2, ...]
    const byDay = {};
    (groupTrainingPreferences || []).forEach((p) => {
      const day = p.dayName || p.day || '';
      const time = p.time || '';
      if (!day || !time) return;
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(time);
    });
    const dayOrder = ['Pirmadienis', 'Antradienis', 'Trečiadienis', 'Ketvirtadienis', 'Penktadienis', 'Šeštadienis', 'Sekmadienis'];
    const preferredTimesText =
      Object.keys(byDay).length > 0
        ? dayOrder
            .filter((d) => byDay[d])
            .map((day) => `${day}\n${(byDay[day] || []).sort().join(', ')}`)
            .join('\n\n')
        : 'Nenurodyta';
    const preferredTimesHtml =
      Object.keys(byDay).length > 0
        ? dayOrder
            .filter((d) => byDay[d])
            .map((day) => `${escapeHtml(day)}<br/>${(byDay[day] || []).sort().map((t) => escapeHtml(t)).join(', ')}`)
            .join('<br/><br/>')
        : 'Nenurodyta';

    const adminText = `Naujas pageidavimas grupinėms treniruotėms

Vardas: ${fullName || '—'}
Telefonas: ${phoneNumber || '—'}
El. paštas: ${email || '—'}
NTRP lygis: ${ntrpLevel || '—'}
Kiek kartų per savaitę nori treniruotis: ${trainingsPerWeek ? `${trainingsPerWeek} kartų` : '—'}

Pageidaujami laikai ir dienos:
${preferredTimesText}`;

    const adminHtml = `<!DOCTYPE html><html><body style="font-family: sans-serif;">
<p><strong>Naujas pageidavimas grupinėms treniruotėms</strong></p>
<p>Vardas: ${escapeHtml(fullName) || '—'}<br/>
Telefonas: ${phoneNumber ? `<a href="tel:${escapeHtml(phoneNumber)}">${escapeHtml(phoneNumber)}</a>` : '—'}<br/>
El. paštas: ${email ? `<a href="mailto:${escapeHtml(email)}">${escapeHtml(email)}</a>` : '—'}<br/>
NTRP lygis: ${escapeHtml(ntrpLevel) || '—'}<br/>
Kiek kartų per savaitę nori treniruotis: ${trainingsPerWeek ? `${escapeHtml(trainingsPerWeek)} kartų` : '—'}</p>
<p><strong>Pageidaujami laikai ir dienos:</strong></p>
<p>${preferredTimesHtml}</p>
</body></html>`;

    await sendEmail(INFO_EMAIL, {
      subject: `Grupinės treniruotės: ${fullName || email}`,
      text: adminText,
      html: adminHtml
    }, null, fullName || 'group_training', EMAIL_FROM_NAME);

    const trainingsPerWeekLine = trainingsPerWeek
      ? `\nKartų per savaitę: ${trainingsPerWeek}\n`
      : '\n';
    const userText = `Sveiki${fullName ? ` ${fullName}` : ''}!

Ačiū – Jūsų pageidavimus grupinėms treniruotėms gavome. 🎾${trainingsPerWeekLine}
Kai turėsime pakankamai norinčių žaidėjų startuoti grupinę treniruotę – susisieksime. 🎾

Su pagarba,
TenisoUžsiėmimai.lt`;

    if (email) {
      await sendEmail(email, {
        subject: 'Grupinės treniruotės – pageidavimai gauti',
        text: userText
      }, null, fullName || 'group_training', EMAIL_FROM_NAME);
    }
  } catch (error) {
    console.error('Error creating group training emails:', error);
    throw error;
  }
};

// Submit group training preference: update profile and send emails via Firestore mail collection (Trigger Email extension)
export const submitGroupTrainingPreference = async (userId, data) => {
  const { ntrpLevel, trainingsPerWeek, groupTrainingPreferences, fullName, phoneNumber, email } = data;
  const updates = {
    groupTrainingPreferences: groupTrainingPreferences || [],
    groupTrainingSubmittedAt: Timestamp.now()
  };
  if (ntrpLevel != null && ntrpLevel !== '') {
    updates.ntrpLevel = ntrpLevel;
  }
  if (trainingsPerWeek != null && trainingsPerWeek !== '') {
    updates.trainingsPerWeek = trainingsPerWeek;
  }
  if (fullName != null && fullName !== '') updates.fullName = fullName;
  if (phoneNumber != null && phoneNumber !== '') updates.phoneNumber = phoneNumber;
  if (email != null && email !== '') updates.email = email;
  await updateUserProfile(userId, updates);
  await createGroupTrainingEmails({
    fullName,
    phoneNumber,
    email,
    ntrpLevel: ntrpLevel || '',
    trainingsPerWeek: trainingsPerWeek ?? '',
    groupTrainingPreferences: groupTrainingPreferences || []
  });
};

// Clear group training preferences (no emails sent)
export const clearGroupTrainingPreference = async (userId) => {
  await updateUserProfile(userId, {
    groupTrainingPreferences: [],
    trainingsPerWeek: '',
    groupTrainingSubmittedAt: null
  });
}; 